<template>
  <v-app>
    <!-- 3D Background -->
    <ThreeDBackground v-if="!hideNavigation" />

    <!-- Animated Cursor -->
    <AnimatedCursor />

    <!-- Main Navigation Bar - Restructured -->
    <div
      v-if="!hideNavigation"
      class="app-bar"
    >
      <div class="header-container">
        <!-- Primary Header Row -->
        <div class="header-top">
          <!-- Left: Logo Section -->
          <div class="header-left">
            <router-link to="/" class="logo-link">
              <div class="logo-icon-wrapper">
                <img src="/buzz-logo.png" alt="BuzzCentral Logo" class="logo-image" />
              </div>
              <div class="logo-text">
                <div class="logo-title">BuzzCentral</div>
              </div>
            </router-link>
          </div>

          <!-- Center: Navigation Menu - Buzz Central Style -->
          <div class="header-center">
            <nav class="main-nav buzz-nav-tabs">
              <router-link
                v-for="item in mainNavigationItems"
                :key="item.title"
                :to="item.to"
                class="buzz-tab-btn"
                :class="{ 'buzz-tab-active': isActiveRoute(item.to) }"
              >
                {{ item.title }}
              </router-link>
            </nav>
          </div>

          <!-- Right: Auth Buttons -->
          <div class="header-right">
            <div class="auth-buttons">
              <template v-if="user">
                <!-- Notification Bell -->
                <NotificationBell />

                <!-- User Menu -->
                <v-menu offset-y>
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      class="user-menu-btn"
                      variant="outlined"
                    >
                      <v-avatar size="28" class="user-avatar-small">
                        <v-img
                          v-if="user.avatar_url"
                          :src="user.avatar_url"
                          :alt="user.full_name || user.username"
                        />
                        <v-icon v-else size="18">mdi-account-circle</v-icon>
                      </v-avatar>
                      <span class="user-name-small hide-mobile">{{ user.username || 'User' }}</span>
                      <v-icon size="16">mdi-chevron-down</v-icon>
                    </v-btn>
                  </template>

                  <v-list class="user-dropdown" density="compact">
                    <v-list-item to="/profile" class="dropdown-item">
                      <template v-slot:prepend>
                        <v-icon size="18">mdi-account</v-icon>
                      </template>
                      <v-list-item-title>My Profile</v-list-item-title>
                    </v-list-item>

                    <v-list-item v-if="user?.is_creator" to="/creator-dashboard" class="dropdown-item">
                      <template v-slot:prepend>
                        <v-icon size="18">mdi-chart-line</v-icon>
                      </template>
                      <v-list-item-title>Dashboard</v-list-item-title>
                    </v-list-item>

                    <v-list-item to="/social/instagram" class="dropdown-item">
                      <template v-slot:prepend>
                        <v-icon size="18" color="pink">mdi-instagram</v-icon>
                      </template>
                      <v-list-item-title>Connect Instagram</v-list-item-title>
                    </v-list-item>

                    <v-divider class="my-1" />

                    <v-list-item @click="logout" class="dropdown-item logout-item">
                      <template v-slot:prepend>
                        <v-icon size="18">mdi-logout</v-icon>
                      </template>
                      <v-list-item-title>Logout</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
              <template v-else>
                <!-- Sign In Button -->
                <v-btn
                  class="sign-in-btn"
                  variant="outlined"
                  @click="login"
                >
                  Sign In
                </v-btn>

                <!-- Join Free Button -->
                <v-btn
                  class="join-free-btn"
                  @click="() => $router.push('/register')"
                >
                  Join Free
                </v-btn>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Breaking Ticker -->
    <div v-if="!hideNavigation" class="breaking-ticker">
      <div class="ticker-label">
        <span class="pulse-dot"></span>
        BUZZ UPDATE
      </div>
      <div class="ticker-content">
        <div class="ticker-items">
          <span class="ticker-item">🎉 NEW: 50+ AI Courses Just Launched!</span>
          <span class="ticker-item">🏆 Success Story: Student Earns ₹1 Lakh in First Month!</span>
          <span class="ticker-item">🚀 Bheem Flow 2.0: 30+ New AI Nodes Added!</span>
          <span class="ticker-item">🎙️ Top Podcast: "From Zero to Hero" Hits 1M Downloads!</span>
          <span class="ticker-item">📚 Flash Sale: 50% OFF on All Premium Courses Today!</span>
          <span class="ticker-item">💡 Community Milestone: 50,000+ Active Learners!</span>
          <span class="ticker-item">🎬 NEW: Instagram Reels Creation Masterclass!</span>
          <span class="ticker-item">🔥 Trending: No-Code Automation Workshop Seats Filling Fast!</span>
          <!-- Duplicate for seamless loop -->
          <span class="ticker-item">🎉 NEW: 50+ AI Courses Just Launched!</span>
          <span class="ticker-item">🏆 Success Story: Student Earns ₹1 Lakh in First Month!</span>
          <span class="ticker-item">🚀 Bheem Flow 2.0: 30+ New AI Nodes Added!</span>
          <span class="ticker-item">🎙️ Top Podcast: "From Zero to Hero" Hits 1M Downloads!</span>
          <span class="ticker-item">📚 Flash Sale: 50% OFF on All Premium Courses Today!</span>
          <span class="ticker-item">💡 Community Milestone: 50,000+ Active Learners!</span>
          <span class="ticker-item">🎬 NEW: Instagram Reels Creation Masterclass!</span>
          <span class="ticker-item">🔥 Trending: No-Code Automation Workshop Seats Filling Fast!</span>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <v-main :class="['main-content', { 'auth-page-layout': hideNavigation }]">
      <router-view />
    </v-main>

    <!-- Loading Overlay -->
    <v-overlay v-if="loading" persistent>
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      />
    </v-overlay>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      :multi-line="snackbar.multiLine"
    >
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn
          text
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useNotificationStore } from '@/store/notifications'
import NotificationBell from '@/components/notifications/NotificationBell.vue'
import ThreeDBackground from '@/components/common/ThreeDBackground.vue'
import AnimatedCursor from '@/components/common/AnimatedCursor.vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const notificationStore = useNotificationStore()

// Reactive data
const loading = ref(false)
const searchQuery = ref('')
const snackbar = ref({
  show: false,
  message: '',
  color: 'info',
  timeout: 4000,
  multiLine: false
})

// Main Navigation items (community-first with more engagement tabs)
const mainNavigationItems = [
  { title: 'Home', to: '/' },
  { title: 'Discussions', to: '/discussions' },
  { title: 'Groups', to: '/groups' },
  { title: 'Events', to: '/events' },
  { title: 'Reels', to: '/reels' },
  { title: 'AI News', to: '/news' },
  { title: 'Resources', to: '/resources' },
  { title: 'Creators', to: '/creators' },
  { title: 'Leaderboard', to: '/leaderboard' }
]

// Social media links
const socialLinks = [
  { name: 'Instagram', url: 'https://instagram.com/aibuzzcentral_', icon: 'mdi-instagram' },
  { name: 'Twitter', url: 'https://twitter.com/aibuzzcentral', icon: 'mdi-twitter' },
  { name: 'LinkedIn', url: 'https://linkedin.com/company/aibuzzcentral', icon: 'mdi-linkedin' },
  { name: 'YouTube', url: 'https://youtube.com/@aibuzzcentral', icon: 'mdi-youtube' },
  { name: 'GitHub', url: 'https://github.com/aibuzzcentral', icon: 'mdi-github' },
  { name: 'Discord', url: 'https://discord.gg/aibuzzcentral', icon: 'mdi-discord' }
]

// Computed properties
const user = computed(() => authStore.user)
const unreadNotifications = computed(() => notificationStore.unreadCount)
const hideNavigation = computed(() => route.meta?.hideNavigation || false)
const hideFooter = computed(() => route.meta?.hideFooter || false)

// Methods
const login = () => {
  // Redirect to login page
  router.push({ name: 'Login', query: { redirect: route.fullPath } })
}

const logout = () => {
  authStore.logout()
  router.push('/')
}

const createContent = () => {
  router.push('/create')
}

const isActiveRoute = (path: string) => {
  return route.path === path
}

// Initialize app
onMounted(async () => {
  loading.value = true
  try {
    // Check if user is already authenticated
    if (authStore.accessToken) {
      await authStore.fetchUser()
    }
  } catch (error) {
    console.error('Failed to initialize user:', error)
  } finally {
    loading.value = false
  }
})
</script>

<style lang="scss">
@import '@/styles/variables';

// ==========================================
// APP BAR / HEADER - RESTRUCTURED WITH 3D EFFECTS
// ==========================================
.app-bar {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
  border-bottom: 1px solid rgba(118, 75, 162, 0.15);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: var(--appbar-height);
  z-index: var(--z-sticky);
  box-shadow:
    0 8px 32px rgba(102, 126, 234, 0.12),
    inset 0 -1px 0 rgba(255, 255, 255, 0.5);
  transition: all var(--transition-base);
  transform-style: preserve-3d;

  .header-container {
    padding: var(--space-3) var(--space-6);
    max-width: var(--container-max-width);
    margin: 0 auto;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: var(--space-2);
  }

  // ==========================================
  // PRIMARY HEADER ROW
  // ==========================================
  .header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-6);
  }

  // Left Section - Logo with 3D effects
  .header-left {
    .logo-link {
      display: flex;
      align-items: center;
      gap: 12px;
      text-decoration: none;
      transition: all var(--transition-base);
      transform-style: preserve-3d;

      &:hover {
        transform: translateY(-2px) translateZ(10px);

        .logo-icon-wrapper {
          transform: rotateY(360deg) scale(1.05);
          box-shadow:
            0 10px 30px rgba(102, 126, 234, 0.4),
            0 0 40px rgba(118, 75, 162, 0.3);
        }
      }

      .logo-icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 45px;
        height: 45px;
        background: transparent;
        border-radius: 12px;
        transition: all var(--transition-slow);
        transform-style: preserve-3d;
        box-shadow: 0 4px 15px rgba(118, 75, 162, 0.2);
        position: relative;

        &::before {
          content: '';
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          background: rgba(118, 75, 162, 0.3);
          border-radius: 14px;
          opacity: 0;
          filter: blur(10px);
          transition: opacity var(--transition-base);
          z-index: -1;
        }

        &:hover::before {
          opacity: 1;
        }

        .logo-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
        }
      }

      .logo-text {
        .logo-title {
          font-size: 1.3rem;
          font-weight: 700;
          background: var(--gradient-primary);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1.2;
        }
      }
    }
  }

  // Center Section - Main Navigation (Buzz Central Style)
  .header-center {
    flex: 1;
    display: flex;
    justify-content: center;

    .main-nav {
      display: flex;
      align-items: center;
    }

    .buzz-nav-tabs {
      display: flex;
      gap: 10px;
      background: #f3f4f6;
      padding: 5px;
      border-radius: 15px;
      box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    .buzz-tab-btn {
      padding: 10px 20px;
      border: none;
      background: transparent;
      border-radius: 10px;
      font-weight: 600;
      font-size: var(--fs-sm);
      color: var(--text-secondary);
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      text-decoration: none;
      position: relative;
      overflow: hidden;
      white-space: nowrap;

      // Subtle hover state
      &:hover:not(.buzz-tab-active) {
        color: var(--text-primary);
        background: rgba(255, 255, 255, 0.5);
      }

      // Active state - matches Buzz Central design
      &.buzz-tab-active {
        background: var(--white);
        color: var(--primary-purple);
        box-shadow:
          0 2px 8px rgba(0, 0, 0, 0.08),
          0 1px 3px rgba(0, 0, 0, 0.05);
        font-weight: 700;
      }
    }

    @media (max-width: 1024px) {
      display: none;
    }
  }

  // Right Section - Auth Buttons
  .header-right {
    .auth-buttons {
      display: flex;
      align-items: center;
      gap: 15px;
    }

    .sign-in-btn {
      background: transparent !important;
      color: var(--primary-blue) !important;
      border: 2px solid var(--primary-blue) !important;
      border-radius: 25px !important;
      font-weight: 600 !important;
      text-transform: none !important;
      padding: 10px 20px !important;
      transition: all var(--transition-base);
      transform-style: preserve-3d;
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 0;
        height: 0;
        border-radius: 50%;
        background: var(--gradient-primary);
        transform: translate(-50%, -50%);
        transition: width 0.5s, height 0.5s;
        z-index: -1;
      }

      &:hover {
        background: var(--primary-blue) !important;
        color: var(--white) !important;
        transform: translateY(-2px) translateZ(5px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);

        &::before {
          width: 300px;
          height: 300px;
        }
      }
    }

    .join-free-btn {
      background: var(--gradient-primary) !important;
      color: var(--white) !important;
      border: none !important;
      border-radius: 25px !important;
      font-weight: 600 !important;
      text-transform: none !important;
      padding: 10px 25px !important;
      box-shadow:
        0 4px 15px rgba(102, 126, 234, 0.3),
        inset 0 -2px 8px rgba(0, 0, 0, 0.1),
        inset 0 2px 8px rgba(255, 255, 255, 0.3);
      transition: all var(--transition-base);
      transform-style: preserve-3d;
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: linear-gradient(
          45deg,
          transparent 30%,
          rgba(255, 255, 255, 0.3) 50%,
          transparent 70%
        );
        transform: rotate(45deg);
        transition: all 0.6s;
      }

      &:hover {
        box-shadow:
          0 8px 25px rgba(102, 126, 234, 0.4),
          0 0 40px rgba(118, 75, 162, 0.3),
          inset 0 -2px 8px rgba(0, 0, 0, 0.1),
          inset 0 2px 8px rgba(255, 255, 255, 0.4);
        transform: translateY(-3px) translateZ(10px) scale(1.05);

        &::before {
          left: 100%;
        }
      }

      &:active {
        transform: translateY(-1px) translateZ(5px) scale(1.02);
      }
    }

    .user-menu-btn {
      background: rgba(255, 255, 255, 0.5) !important;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border: 2px solid var(--primary-blue) !important;
      border-radius: 25px !important;
      font-weight: 600 !important;
      text-transform: none !important;
      padding: 8px 16px !important;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all var(--transition-base);
      transform-style: preserve-3d;
      box-shadow: 0 2px 10px rgba(102, 126, 234, 0.1);

      .user-avatar-small {
        border: 2px solid var(--gray-200);
        transition: all var(--transition-base);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      .user-name-small {
        font-size: var(--fs-sm);
        color: var(--text-primary);
        transition: color var(--transition-base);
      }

      &:hover {
        background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1)) !important;
        transform: translateY(-2px) translateZ(5px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.2);

        .user-avatar-small {
          transform: scale(1.1) rotateZ(5deg);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }
      }
    }
  }

  // User Dropdown
  .user-dropdown {
    min-width: 200px;
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-xl);
    border: 1px solid var(--gray-200);
    overflow: hidden;

    .dropdown-item {
      transition: all var(--transition-fast);

      &:hover {
        background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
      }

      &.logout-item {
        color: var(--error);

        &:hover {
          background: rgba(245, 87, 108, 0.05);
        }
      }
    }
  }

}

// ==========================================
// MAIN CONTENT
// ==========================================
// Default: add padding for fixed header + ticker
.v-main,
.main-content {
  padding-top: calc(var(--header-total-height) + 45px) !important;
}

// Home page: no padding, let content handle it
.v-main:has(.buzz-central-home),
.v-main:has(.community-home-logged-in) {
  padding-top: 0 !important;
}

// Auth pages - full viewport, no padding
.v-main.auth-page-layout,
.main-content.auth-page-layout {
  padding: 0 !important;

  :deep(.v-main__wrap) {
    padding: 0 !important;
    margin: 0 !important;
  }
}

// Ensure v-main's internal wrapper also respects the padding
:deep(.v-main__wrap) {
  padding-top: 0 !important;
  margin: 0 !important;
}

.footer-link {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 0.875rem;
  
  &:hover {
    color: white;
    text-decoration: underline;
  }
}

// Global scrollbar styling
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
  
  &:hover {
    background: #a8a8a8;
  }
}

// ==========================================
// RESPONSIVE ADJUSTMENTS
// ==========================================

// Tablet and below
@media (max-width: 1024px) {

  .app-bar {
    .header-container {
      padding: var(--space-2) var(--space-4);
      gap: var(--space-2);
    }

    .header-top {
      gap: var(--space-3);
    }

    .header-center {
      display: none; // Hide nav tabs on tablet and below
    }
  }
}

// Mobile
@media (max-width: 768px) {

  .app-bar {
    .header-container {
      padding: var(--space-2) var(--space-3);
      gap: var(--space-1);
    }

    .header-top {
      grid-template-columns: auto auto;
      gap: var(--space-2);

      .header-center {
        display: none;  // Hide search on mobile
      }
    }

    .header-left {
      .logo-link {
        gap: var(--space-2);

        .logo-icon-wrapper {
          width: 36px;
          height: 36px;
        }

        .logo-text {
          .logo-title {
            font-size: var(--fs-base);
          }

          .logo-subtitle {
            display: none;
          }
        }
      }
    }

    .header-right {
      .action-buttons {
        gap: var(--space-1);
      }

      .action-btn {
        &.create-btn {
          padding: var(--space-1) var(--space-2);
          font-size: var(--fs-xs);

          .btn-text {
            display: none;
          }
        }

        &.user-btn {
          padding: var(--space-1);

          .user-name {
            display: none;
          }

          .dropdown-icon {
            display: none;
          }
        }
      }
    }

    .header-nav {
      padding-top: var(--space-1);
      padding-bottom: var(--space-1);

      .nav-tabs {
        gap: var(--space-1);
      }

      .nav-tab {
        padding: 6px var(--space-2);
        font-size: var(--fs-xs);

        .nav-icon {
          font-size: 16px !important;
        }
      }
    }
  }

  .hide-mobile {
    display: none !important;
  }
}

// Small mobile
@media (max-width: 480px) {
  .app-bar {
    .header-left {
      .logo-link {
        .logo-icon-wrapper {
          width: 38px;
          height: 38px;
        }
      }
    }

    .header-nav {
      .nav-tab {
        padding: var(--space-1) var(--space-2);
        gap: var(--space-1);

        .nav-label {
          font-size: 11px;
        }
      }
    }
  }
}

// ==========================================
// VUETIFY OVERRIDES
// ==========================================
// Ensure Vuetify doesn't add its own padding to v-main
:deep(.v-main) {
  padding-top: calc(var(--header-total-height) + 45px) !important;
}

// Ensure v-main's internal wrapper doesn't add extra padding
:deep(.v-main__wrap) {
  padding-top: 0 !important;
}

// ==========================================
// GLOBAL BUTTON ENHANCEMENTS
// ==========================================
:deep(.btn-gradient) {
  background: var(--gradient-primary) !important;
  color: var(--white) !important;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.25);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }

  &:hover::before {
    width: 300px;
    height: 300px;
  }
}

:deep(.btn-outline-gradient) {
  border: 2px solid transparent;
  background: linear-gradient(var(--white), var(--white)) padding-box,
              var(--gradient-primary) border-box !important;
  color: var(--primary-blue) !important;
  transition: all var(--transition-base);

  &:hover {
    background: var(--gradient-primary) !important;
    color: var(--white) !important;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.25);
  }
}

// ==========================================
// BREAKING TICKER / NEWS BANNER
// ==========================================
.breaking-ticker {
  position: fixed;
  top: var(--appbar-height);
  left: 0;
  right: 0;
  width: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  z-index: 1025;
  display: flex;
  align-items: center;
  height: 45px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);

  .ticker-label {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 20px;
    background: rgba(0, 0, 0, 0.2);
    height: 100%;
    font-weight: 700;
    font-size: 0.85rem;
    letter-spacing: 0.5px;
    white-space: nowrap;
    border-right: 2px solid rgba(255, 255, 255, 0.3);
    min-width: 160px;
    justify-content: center;

    .pulse-dot {
      width: 10px;
      height: 10px;
      background: #ff4444;
      border-radius: 50%;
      display: inline-block;
      animation: pulse 1.5s ease-in-out infinite;
      box-shadow: 0 0 10px rgba(255, 68, 68, 0.8);
    }
  }

  .ticker-content {
    flex: 1;
    overflow: hidden;
    position: relative;
    height: 100%;
  }

  .ticker-items {
    display: flex;
    align-items: center;
    height: 100%;
    animation: scroll-ticker 60s linear infinite;
    white-space: nowrap;

    &:hover {
      animation-play-state: paused;
    }

    .ticker-item {
      display: inline-flex;
      align-items: center;
      padding: 0 50px;
      font-size: 0.95rem;
      font-weight: 500;
      transition: all 0.3s ease;

      &:hover {
        transform: scale(1.05);
        text-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
      }
    }
  }
}

// Ticker scroll animation
@keyframes scroll-ticker {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

// Pulse animation for the dot
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.3);
    opacity: 0.7;
  }
}

// Adjust main content padding to account for ticker
.v-main,
.main-content {
  padding-top: calc(var(--header-total-height) + 45px) !important;
}

// Home page: no padding, let content handle it
.v-main:has(.buzz-central-home),
.v-main:has(.community-home-logged-in) {
  padding-top: 0 !important;
}

// Responsive adjustments for ticker
@media (max-width: 768px) {
  .breaking-ticker {
    top: var(--appbar-height);
    height: 40px;

    .ticker-label {
      padding: 0 12px;
      font-size: 0.75rem;
      min-width: 120px;

      .pulse-dot {
        width: 8px;
        height: 8px;
      }
    }

    .ticker-items {
      animation: scroll-ticker 40s linear infinite;

      .ticker-item {
        padding: 0 30px;
        font-size: 0.85rem;
      }
    }
  }

  .v-main,
  .main-content {
    padding-top: calc(var(--header-total-height) + 40px) !important;
  }
}

@media (max-width: 480px) {
  .breaking-ticker {
    height: 36px;

    .ticker-label {
      padding: 0 10px;
      font-size: 0.7rem;
      min-width: 100px;
      gap: 6px;
    }

    .ticker-items {
      .ticker-item {
        padding: 0 20px;
        font-size: 0.8rem;
      }
    }
  }

  .v-main,
  .main-content {
    padding-top: calc(var(--header-total-height) + 36px) !important;
  }
}
</style>
